import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatR } from "@/lib/api";

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("btm_cart")) || [];
    } catch {
      return [];
    }
  });
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [settings, setSettings] = useState({ delivery_fee: 85, free_delivery_over: 750 });
  // Which school grade list this cart was built from (drives sales-by-school reporting)
  const [schoolMeta, setSchoolMeta] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("btm_cart_school")) || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    localStorage.setItem("btm_cart", JSON.stringify(items));
  }, [items]);

  useEffect(() => {
    if (schoolMeta) localStorage.setItem("btm_cart_school", JSON.stringify(schoolMeta));
    else localStorage.removeItem("btm_cart_school");
  }, [schoolMeta]);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings((s) => ({ ...s, ...r.data }))).catch(() => {});
  }, []);

  const add = (product, qty = 1, { silent, variant } = {}) => {
    const key = `${product.id}|${variant || ""}`;
    setItems((prev) => {
      const found = prev.find((i) => i.key === key);
      const current = found ? found.qty : 0;
      if (product.stock !== undefined && current + qty > product.stock) {
        toast.error(`Only ${product.stock} in stock for "${product.name}"`);
        return prev;
      }
      if (found) return prev.map((i) => (i.key === key ? { ...i, qty: i.qty + qty } : i));
      return [
        ...prev,
        {
          key,
          id: product.id,
          name: product.name,
          variant: variant || null,
          variant_label: variant ? (product.variants?.[0]?.label || "Option") : null,
          price: product.effective_price ?? product.price,
          image: product.image,
          stock: product.stock,
          qty,
        },
      ];
    });
    if (!silent) toast.success(`Added to cart${variant ? ` — ${variant}` : ""} — ${formatR(product.effective_price ?? product.price)}`);
  };

  const setQty = (key, qty) => {
    if (qty < 1) return;
    setItems((prev) => prev.map((i) => ((i.key || i.id) === key ? { ...i, qty } : i)));
  };

  const remove = (key) => setItems((prev) => prev.filter((i) => (i.key || i.id) !== key));
  const clear = () => { setItems([]); setSchoolMeta(null); };

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.price * i.qty, 0), [items]);
  const count = useMemo(() => items.reduce((s, i) => s + i.qty, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, add, setQty, remove, clear, subtotal, count, drawerOpen, setDrawerOpen, settings, schoolMeta, setSchoolMeta }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
